<!--
  BaseConsentGate

  This component is intentionally content-agnostic so it can wrap any type
  of embedded third-party content.

  Props:
    category   - The Cookiebot consent category required to show the content.
    minHeight  - CSS min-height applied to both the placeholder and the content
                 wrapper to prevent layout shift when consent is given.
-->
<template>
  <div
    class="w-full"
    :style="{ minHeight }"
  >
    <!-- Placeholder: shown until the user accepts the required consent category -->
    <div
      v-if="!hasConsent"
      class="flex flex-col items-center justify-center gap-4 rounded border border-gray-200 bg-gray-50 p-8 text-center"
      :style="{ minHeight }"
    >
      <p class="text-sm text-gray-600">
        This content requires your consent to load.
      </p>
      <button
        type="button"
        class="btn btn--primary"
        @click="openConsentDialog"
      >
        Manage cookie preferences
      </button>
    </div>

    <!-- Content slot: shown once consent has been confirmed -->
    <div
      v-else
      :style="{ minHeight }"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useCookieConsent, type CookieConsentCategory } from '@js/composables/useCookieConsent'

defineOptions({
  name: 'BaseConsentGate',
})

const props = withDefaults(
  defineProps<{
    categories?: CookieConsentCategory[]
    minHeight?: string
  }>(),
  {
    categories: () => ['preferences'] as CookieConsentCategory[],
    minHeight: '400px',
  },
)

const { hasConsent, openConsentDialog } = useCookieConsent(props.categories)
</script>
